import React, { useEffect } from "react"
import {
  dealerEnquiryURL,
  footerUrl,
  headerUrl,
  previewContentPrefix,
  dealerEnquiryCitiesURL,
} from "@/constants"
import { useDispatch } from "react-redux"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import AemGrid from "@/components/AemGrid"
import {
  buildUrlForAemApim,
  filterBackEndJson,
  getDataLayerObj,
} from "@/utils/helper"
import {
  aemApimAxiosInstance,
  aemAxiosInstance,
  aemPreviewAxiosInstance,
} from "@/constants/api"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import usePageAuth from "@/hooks/usePageAuth"
import BreadCrumb from "@/components/Default/BreadCrumb"
import { getStatesAndCitiesFromServer } from "@/utils/findStore"
import DealershipEnquiryView from "@/components/DealershipEnquiry/DealershipEnquiryView/index"
import { breadcrumbAnalytics } from "@/components/DealershipEnquiry/analytics"
import {
  setDatalayerPageName,
  setGbhDataLayerCustom,
} from "@/store/features/genericSlice"

const DealershipEnquiryPage = ({
  tmpData: data,
  citiesTmpData: citiesData,
  headertmp: header,
  footertmp: footer,
  globalScript,
  footerScript,
  analysticsScript,
  analyticsData,
  datalayerPageName,
  gbhDataLayerCustom,
  stateWithCities,
}) => {
  usePageAuth()

  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(setDatalayerPageName(datalayerPageName))
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    return () => {
      dispatch(setDatalayerPageName(""))
      dispatch(setGbhDataLayerCustom(""))
    }
  }, [])

  return (
    <>
      <GlobalPageHeader
        header={header}
        title={data?.title}
        globalScripting={globalScript}
        analysticsScript={analysticsScript}
      />
      <AemGrid
        data={data}
        count={0}
        breadCrumb={(data, fullData) => (
          <BreadCrumb
            data={data}
            fullData={fullData}
            analyticsEvent={breadcrumbAnalytics}
          />
        )}
        landing={data => (
          <DealershipEnquiryView data={data} citiesData={citiesData} />
        )}
      />
      <GlobalPageFooter footer={footer} footerScripting={footerScript} />
    </>
  )
}

/**
 * Get ServerSideProps together.
 * @param {Object} request The first number.
 * @return {object} The props.
 */
export async function getServerSideProps({ req, locale }) {
  // await i18n?.reloadResources()
  const isPreviewMode = req.cookies["aem-author-preview-mode"]
  const stateWithCitiesResult = await getStatesAndCitiesFromServer()
  const stateWithCities = stateWithCitiesResult.result.data
  const { data } = isPreviewMode
    ? await aemPreviewAxiosInstance({
        url: previewContentPrefix + dealerEnquiryURL,
        method: "GET",
      })
    : await aemApimAxiosInstance({
        url: buildUrlForAemApim(dealerEnquiryURL),
        method: "GET",
      })

  const { data: citiesData } = await aemApimAxiosInstance({
    url: buildUrlForAemApim(dealerEnquiryCitiesURL),
    method: "GET",
  })

  const { data: header } = isPreviewMode
    ? await aemPreviewAxiosInstance({
        url: `${data.headerExpFrg ? data.headerExpFrg : headerUrl}.model.json`,
        method: "GET",
      })
    : await aemApimAxiosInstance({
        url: data.headerExpFrg
          ? buildUrlForAemApim(`${data.headerExpFrg}.model.json`)
          : buildUrlForAemApim(`${headerUrl}.model.json`),
        method: "get",
      })

  const { data: footer } = isPreviewMode
    ? await aemPreviewAxiosInstance({
        url: `${data.footerExpFrg ? data.footerExpFrg : footerUrl}.model.json`,
        method: "GET",
      })
    : await aemApimAxiosInstance({
        url: data.footerExpFrg
          ? buildUrlForAemApim(`${data.footerExpFrg}.model.json`)
          : buildUrlForAemApim(`${footerUrl}.model.json`),
        method: "GET",
      })

  const globalScript = data?.globalScript
  const footerScript = data?.footerScript ? data.footerScript : null
  const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null

  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }

  const analysticsScript = data?.adobeAnalytic || null
  const datalayerPageName = data?.datalayerPageName || null

  let tmpData = {}
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }
  tmpData = filterBackEndJson(tmpData)
  const analyticsData = data?.data
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  return {
    props: {
      tmpData,
      headertmp,
      footertmp,
      globalScript,
      analyticsData,
      footerScript,
      topHeadScript,
      analysticsScript,
      gbhDataLayerCustom,
      datalayerPageName,
      stateWithCities,
      citiesTmpData: citiesData,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}

export default DealershipEnquiryPage
